<script>
  // Wants Board page (ADR-0011): what does this group / my whole network
  // want, partitioned by who can supply it. Scope rides the browse group
  // dropdown (browseState.selectedGroupId), like Search. Bespoke sectioned
  // list — the CardCollection shell assumes ONE homogeneous list — reusing
  // the shell's row component and header idioms.
  import WantsSection from './WantsSection.svelte';
  import WantsCardDetail from './WantsCardDetail.svelte';
  import {
    WANTS_SECTIONS,
    WANTS_SORT_OPTIONS,
    scopeFromGroupId,
  } from '../../lib/logic/wants-board-logic.js';
  import {
    browseState,
    activeSubgroupIds,
    groupUrlParams,
    syncSelectionFromRoute,
  } from '../../lib/stores/browse.svelte.js';
  import { route, navigate } from '../../lib/stores/router.svelte.js';
  import { groupsState } from '../../lib/stores/groups.svelte.js';
  import { untrack } from 'svelte';
  import {
    authState,
    isUserAnonymous,
    initiateDiscordOAuth,
  } from '../../lib/stores/auth.svelte.js';

  let sort = $state(route.params.sort || 'most-wanted');
  let q = $state(route.params.q || '');
  let debouncedQ = $state(route.params.q || '');
  let counts = $state(null);
  let detailItem = $state(null);
  let debounceTimer = null;
  $effect(() => () => clearTimeout(debounceTimer));

  // Keep local state in sync with route.params reactively — the component is
  // NOT remounted on same-view navigation (back/forward, a second deep link),
  // so mount-only init would go stale. Converging comparisons make our own
  // URL-mirror effect below a no-op, and comparing the URL q against
  // debouncedQ (not q) protects in-flight typing from being clobbered
  // mid-debounce. `syncSelectionFromRoute` is untracked: it both reads AND
  // writes `browseState.selectedSubgroupIds` (it always replaces it with a
  // fresh object when the URL has a `sub`, even if the ids are unchanged),
  // so without `untrack` this effect ends up depending on the very state it
  // just wrote, and Svelte throws effect_update_depth_exceeded on any
  // `?group=&sub=` deep link (regression, caught while verifying finding 2).
  $effect(() => {
    untrack(() => syncSelectionFromRoute(route.params));
    const urlSort = route.params.sort || 'most-wanted';
    const urlQ = route.params.q || '';
    if (urlSort !== sort) sort = urlSort;
    if (urlQ !== debouncedQ) {
      q = urlQ;
      debouncedQ = urlQ;
    }
  });

  const subgroupIds = $derived(activeSubgroupIds());
  const scopeParams = $derived(scopeFromGroupId(browseState.selectedGroupId, subgroupIds));
  const currencySymbol = '$';

  // One URL builder, reused by both the synchronous handlers below and the
  // reactive mirror effect. A plain memo (not `$state`) of the last-written
  // params short-circuits when nothing actually changed — needed because
  // `syncSelectionFromRoute` above always writes a fresh `selectedSubgroupIds`
  // object (by reference) when the URL has a `sub`, even when its contents
  // are unchanged; without this guard that reference churn and this
  // function's own `navigate()` call ping-pong into an
  // effect_update_depth_exceeded loop on any `?group=&sub=` deep link. Seeded
  // from the URL we were actually mounted with (not `null`), so the mirror
  // effect's first run is a no-op when the computed params already match —
  // otherwise mounting `/wants?group=&sub=` always fires one redundant
  // `history.replaceState` for an identical URL.
  let lastMirroredParams = JSON.stringify(route.params);
  function syncUrl() {
    /** @type {Record<string, string>} */
    const params = { ...groupUrlParams() };
    if (sort !== 'most-wanted') params.sort = sort;
    if (debouncedQ) params.q = debouncedQ;
    const key = JSON.stringify(params);
    if (key === lastMirroredParams) return;
    lastMirroredParams = key;
    navigate('wants', params, true);
  }

  function onFilterInput(value) {
    q = value;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debouncedQ = q.trim();
      syncUrl();
    }, 300);
  }

  function onSortChange(value) {
    sort = value;
    syncUrl();
  }

  // Mirror the group picker's EXTERNAL toggles (browseState/subgroupIds) into
  // the URL — there's no local handler to hook a syncUrl() call into for
  // those, since the picker lives in BrowseTabBar and mutates the shared
  // store directly. Sort/q keep their synchronous syncUrl() calls above
  // instead of relying on this effect for them: firing reactively on every
  // `sort`/`debouncedQ` change would race the sync effect above, which
  // reverts `sort`/`debouncedQ` back toward the URL whenever they disagree —
  // if this effect ran first, on the same tick as a user's sort change, the
  // URL would still show the OLD sort, so the sync effect would see a
  // mismatch and revert `sort` before the new value ever reached the URL.
  // Calling syncUrl() synchronously from the handler updates the URL before
  // that effect can run at all, avoiding the race; this effect only needs to
  // cover the toggle path the handlers can't reach.
  $effect(() => {
    void browseState.selectedGroupId;
    void subgroupIds;
    syncUrl();
  });

  function sectionCount(key) {
    return counts ? counts[key] : null;
  }
</script>

{#if !authState.ready}
  <div class="wants-page"><p class="wants-loading">Loading…</p></div>
{:else if isUserAnonymous()}
  <div class="wants-auth-gate">
    <div class="wants-auth-card">
      <h2>Sign in to see the wants board</h2>
      <p>Sign in with Discord to see what people in your groups are looking for.</p>
      <button class="btn-discord" onclick={() => initiateDiscordOAuth()}
        >Sign in with Discord</button
      >
    </div>
  </div>
{:else if groupsState.userGroups.length === 0}
  <div class="wants-auth-gate">
    <div class="wants-auth-card">
      <h2>No groups yet</h2>
      <p>The wants board shows what your group-mates want. Join or create a group first.</p>
      <button class="btn-plain" onclick={() => navigate('groups')}>Go to Groups</button>
    </div>
  </div>
{:else}
  <div class="wants-page">
    <div class="wants-header">
      <input
        type="text"
        class="wants-filter"
        placeholder="Filter wanted cards…"
        value={q}
        oninput={(e) => onFilterInput(e.currentTarget.value)}
      />
      <select
        class="wants-sort"
        value={sort}
        onchange={(e) => onSortChange(e.currentTarget.value)}
        title="Sort"
      >
        {#each WANTS_SORT_OPTIONS as opt (opt.value)}
          <option value={opt.value}>{opt.label}</option>
        {/each}
      </select>
    </div>

    {#each WANTS_SECTIONS as s (s.key)}
      <WantsSection
        section={s.key}
        title={s.title}
        {scopeParams}
        {sort}
        q={debouncedQ}
        count={sectionCount(s.key)}
        {currencySymbol}
        onCounts={(c) => (counts = c)}
        onCardClick={(item) => (detailItem = item)}
      />
    {/each}
  </div>

  {#if detailItem}
    <WantsCardDetail
      item={detailItem}
      {scopeParams}
      {currencySymbol}
      onClose={() => (detailItem = null)}
    />
  {/if}
{/if}

<style>
  .wants-page {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: var(--color-bg);
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: var(--space-md) var(--space-xl) var(--space-xl);
  }
  .wants-header {
    display: flex;
    gap: var(--space-sm);
    margin-bottom: var(--space-lg);
  }
  .wants-loading {
    font-size: 14px;
    color: var(--color-text-tertiary);
    padding: var(--space-md) 0;
  }
  .wants-filter {
    flex: 1;
    padding: 10px 14px;
    font-size: 14px;
    font-family: var(--font-sans);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg-surface);
    color: var(--color-text);
  }
  .wants-sort {
    padding: 8px 12px;
    font-size: 13px;
    font-family: var(--font-sans);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    background: var(--color-bg-surface);
    color: var(--color-text);
  }
  .wants-auth-gate {
    min-height: 60vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
  }
  .wants-auth-card {
    background: var(--color-bg-surface);
    border-radius: 20px;
    padding: 40px 32px;
    box-shadow: 0 4px 24px rgba(40, 30, 15, 0.1);
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    max-width: 400px;
  }
  .wants-auth-card h2 {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--color-text);
    margin: 0;
  }
  .wants-auth-card p {
    font-size: 15px;
    color: var(--color-text-secondary);
    margin: 0;
    line-height: 1.5;
  }
  .btn-discord {
    all: unset;
    cursor: pointer;
    padding: 12px 24px;
    background: #5865f2;
    color: white;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 500;
    font-family: var(--font-sans);
  }
  .btn-discord:hover {
    background: #4752c4;
  }
  .btn-plain {
    all: unset;
    cursor: pointer;
    padding: 10px 20px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    font-size: 14px;
    font-family: var(--font-sans);
    color: var(--color-text);
  }
  @media (max-width: 768px) {
    .wants-page {
      padding: var(--space-sm) var(--space-md) var(--space-md);
    }
  }
</style>
